import { defineStore } from 'pinia'
import { InsuranceCustomerAccount, InsuranceCustomerAccountPrice } from '@team-milva/lab-models/insuranceCustomerAccount'
import type {
  IInsuranceCustomerAccount,
  IInsuranceCustomerAccountAgreementFile,
  IInsuranceCustomerAccountPrice,
  IInsuranceCustomerAccountPriceAnalysisEntry,
  IInsuranceCustomerAccountPriceTatEntry,
  IInsuranceCustomerAccountPriceAddonEntry,
  TInsuranceCustomerAccountPriceDealEntry,
} from '@team-milva/lab-models/insuranceCustomerAccount'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import type { TLabCustomerLocale } from '@team-milva/lab-models/language'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'

type CreateInsuranceCustomerAccountParams = {
  name: string
  currencyCode: TLabManagementCurrencyCode
  pricingSegmentID: string
}

type SaveInsuranceCustomerAccountPriceParams = {
  pricingSegmentID: string | null
  analyses: IInsuranceCustomerAccountPriceAnalysisEntry[]
  tats: IInsuranceCustomerAccountPriceTatEntry[]
  addons: IInsuranceCustomerAccountPriceAddonEntry[]
  deals: TInsuranceCustomerAccountPriceDealEntry[]
  currencyCode: TLabManagementCurrencyCode | null
}

type UpdateInsuranceCustomerAccountParams = {
  name?: string
  activeState?: 'active' | 'inactive'
  customerAccountIDs?: string[]
}

type UpdateInsuranceCustomerAccountGeneralInfoParams = {
  name?: string
  companyDetails?: Partial<IInsuranceCustomerAccount['companyDetails']>
  primaryContactUserID?: string | null
  images?: NonNullable<IInsuranceCustomerAccount['images']>
  files?: { agreements: IInsuranceCustomerAccountAgreementFile[] }
  /**
   * The insurance customer account's Business Central identity — the customer number (edited from the BC
   * mapping list) plus its own payment terms and automatic-invoicing opt-out (the Fakturering tab).
   * Replaces the whole stored object, so every caller carries forward what it does not edit.
   */
  invoicing?: NonNullable<IInsuranceCustomerAccount['invoicing']>
  /** Customer-app account settings, as on a business customer (MIL-402). */
  supportedLocales?: TLabCustomerLocale[]
  defaultLocale?: TLabCustomerLocale
  /**
   * `sendWelcomeEmails` is not here: it is one-way and only turned on through the welcome-email
   * endpoint (the Brugere tab), as for a business customer.
   */
  options?: {
    isProjectsEnabled?: boolean
    isPricesHiddenInCustomerApp?: boolean
  }
}

export const useInsuranceCustomerAccountStore = defineStore('insuranceCustomerAccount', {
  state: () => ({
    allInsuranceCustomerAccounts: [] as InsuranceCustomerAccount[],
    unsubscribeAllInsuranceCustomerAccounts: null as (() => void) | null,
    allInsuranceCustomerAccountPrices: [] as InsuranceCustomerAccountPrice[],
    unsubscribeAllInsuranceCustomerAccountPrices: null as (() => void) | null,
    listStateFilter: 'active' as 'active' | 'inactive',
    searchText: '' as string,
  }),

  getters: {
    allInsuranceCustomerAccountsLoaded: (state): boolean => !!state.unsubscribeAllInsuranceCustomerAccounts,
    allInsuranceCustomerAccountPricesLoaded: (state): boolean =>
      !!state.unsubscribeAllInsuranceCustomerAccountPrices,
  },

  actions: {
    /** Public display mode (no auth): fills the list from the display endpoint's payload, so
     *  display widgets can resolve an insurance customer's own orders by name (MIL-402). */
    hydrateFromPublicData(raw: unknown[]): void {
      this.allInsuranceCustomerAccounts = (
        raw as ConstructorParameters<typeof InsuranceCustomerAccount>[0][]
      ).map((x) => new InsuranceCustomerAccount(x))
    },

    setSearchText(text: string): void {
      this.searchText = text
    },

    getInsuranceCustomerAccounts(): Promise<InsuranceCustomerAccount[]> {
      if (this.allInsuranceCustomerAccountsLoaded) return Promise.resolve(this.allInsuranceCustomerAccounts)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'InsuranceCustomerAccounts'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allInsuranceCustomerAccounts = querySnapshot.docs.map(
            (doc) => new InsuranceCustomerAccount(doc.data() as IInsuranceCustomerAccount)
          )
          if (!resolved) {
            resolved = true
            resolve(this.allInsuranceCustomerAccounts)
          }
        })

        this.unsubscribeAllInsuranceCustomerAccounts = unsubscribe
      })
    },

    async createInsuranceCustomerAccount(params: CreateInsuranceCustomerAccountParams): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/insurance-customer-accounts',
        {
          path: { labAccountID: accountStore.labAccount?.id ?? '' },
          body: {
            name: params.name,
            currencyCode: params.currencyCode,
            pricingSegmentID: params.pricingSegmentID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateInsuranceCustomerAccount(id: string, params: UpdateInsuranceCustomerAccountParams): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            insuranceCustomerAccountID: id,
          },
          body: params,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateInsuranceCustomerAccountGeneralInfo(
      id: string,
      params: UpdateInsuranceCustomerAccountGeneralInfoParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            insuranceCustomerAccountID: id,
          },
          body: {
            ...(params.name !== undefined && { name: params.name }),
            ...(params.companyDetails !== undefined && {
              companyDetails: params.companyDetails,
            }),
            ...(params.primaryContactUserID !== undefined && {
              primaryContactUserID: params.primaryContactUserID,
            }),
            ...(params.images !== undefined && { images: params.images }),
            ...(params.files !== undefined && { files: params.files }),
            ...(params.invoicing !== undefined && { invoicing: params.invoicing }),
            ...(params.supportedLocales !== undefined && {
              supportedLocales: params.supportedLocales,
            }),
            ...(params.defaultLocale !== undefined && {
              defaultLocale: params.defaultLocale,
            }),
            ...(params.options !== undefined && { options: params.options }),
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    getInsuranceCustomerAccountPrices(): Promise<InsuranceCustomerAccountPrice[]> {
      if (this.allInsuranceCustomerAccountPricesLoaded)
        return Promise.resolve(this.allInsuranceCustomerAccountPrices)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'InsuranceCustomerAccountPrices'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allInsuranceCustomerAccountPrices = querySnapshot.docs.map(
            (doc) => new InsuranceCustomerAccountPrice(doc.data() as IInsuranceCustomerAccountPrice)
          )
          if (!resolved) {
            resolved = true
            resolve(this.allInsuranceCustomerAccountPrices)
          }
        })

        this.unsubscribeAllInsuranceCustomerAccountPrices = unsubscribe
      })
    },

    async saveInsuranceCustomerAccountPrice(
      insuranceCustomerAccountID: string,
      params: SaveInsuranceCustomerAccountPriceParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      const body = {
        pricingSegmentID: params.pricingSegmentID,
        currencyCode: params.currencyCode,
        analyses: params.analyses,
        tats: params.tats,
        addons: params.addons,
        deals: params.deals,
      }

      const result = await managementApi(
        'put',
        'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/pricing',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            insuranceCustomerAccountID,
          },
          body,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.unsubscribeAllInsuranceCustomerAccounts?.()
      this.allInsuranceCustomerAccounts = []
      this.unsubscribeAllInsuranceCustomerAccounts = null
      this.unsubscribeAllInsuranceCustomerAccountPrices?.()
      this.allInsuranceCustomerAccountPrices = []
      this.unsubscribeAllInsuranceCustomerAccountPrices = null
      this.listStateFilter = 'active'
      this.searchText = ''
    },
  },
})
